import type { ChartComponentSpec, ComponentSpec, QueryResponseSpec, WidgetResponseSpec } from "../Processing/types";

const FALLBACK_CATEGORIES: Record<string, string[]> = {
  day: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  week: ["Wk 1", "Wk 2", "Wk 3", "Wk 4"],
  month: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  player: ["Player 1", "Player 2", "Player 3", "Player 4", "Player 5"],
  position: ["Position 1", "Position 2", "Position 3", "Position 4"],
  drill: ["Drill 1", "Drill 2", "Drill 3", "Drill 4"],
};
const MOCK_PALETTE = ["#1a73e8", "#ea4335", "#34a853", "#fbbc04", "#9334e6"];

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/**
 * Resolve the x-axis categories from the spec's persisted groupBy and
 * selected subject names. Falls back to day-of-week if nothing is stored.
 */
function resolveCategories(spec: ChartComponentSpec): string[] {
  const groupBy = spec.chart_builder_misc?.groupBy ?? "day";

  const entityTypes = new Set(["player", "position", "drill"]);
  if (entityTypes.has(groupBy)) {
    const allNames: string[] = [];
    for (const s of spec.series) {
      const names = s.series_builder_misc.selectedSubjectNames;
      if (names?.length) allNames.push(...names);
    }
    const unique = [...new Set(allNames)];
    if (unique.length > 0) return unique;
  }

  return FALLBACK_CATEGORIES[groupBy] ?? FALLBACK_CATEGORIES.day;
}

function generateMockSeries(
  queryId: string,
  displayName: string,
  colorIndex: number,
  seed: number,
  categories: string[],
): QueryResponseSpec {
  const rng = seededRandom(seed);
  const hex = MOCK_PALETTE[colorIndex % MOCK_PALETTE.length];
  const base = 100 + rng() * 400;
  const variance = base * 0.25;

  return {
    query_id: queryId,
    name: displayName.toUpperCase() || `SERIES ${colorIndex + 1}`,
    data: categories.map((cat) => ({
      value: Math.round((base + (rng() - 0.5) * 2 * variance) * 10) / 10,
      category: cat,
      hex,
    })),
  };
}

/**
 * Generate a mock WidgetResponseSpec whose query_ids match the spec's
 * series[].queryId values, so the processing pipeline can join them.
 * Categories are derived from groupBy / selectedSubjectNames in the spec.
 */
export function mockResponseFromSpec(
  spec: ChartComponentSpec,
  widgetId: string,
): WidgetResponseSpec {
  let seedBase = 0;
  for (let i = 0; i < widgetId.length; i++) seedBase += widgetId.charCodeAt(i);

  const categories = resolveCategories(spec);

  return {
    widget_id: widgetId,
    series: spec.series.map((s, i) =>
      generateMockSeries(s.queryId, s.displayName, i, seedBase + i * 7919, categories),
    ),
  };
}

export function mockChartSpec(): ComponentSpec {
  return {
    type: "chart",
    display: { title: "Weekly Training Overview", showLegend: true },
    widgetMutations: {
      showAsymmetry: false,
      customRefLines: [
        { value: 400, label: "Target", colorHex: "#ff4d4f" },
      ],
    },
    series: [
      {
        queryId: "q-sprint",
        displayName: "Sprint Distance",
        chartType: "bar",
        yAxis: "left",
        metricIdentifierType: "metric",
        metricAgg: "avg",
        stackOn: null,
        plotMutations: { showTrendline: false },
        series_builder_misc: {
          selectedMetricId: null,
          activeCategory: "GPS",
          showAdvancedDate: false,
          dateRangePreset: "last4weeks",
          inheritDateRange: true,
          inheritPlayers: true,
        },
      },
      {
        queryId: "q-speed",
        displayName: "Top Speed",
        chartType: "line",
        yAxis: "right",
        metricIdentifierType: "metric",
        metricAgg: "avg",
        stackOn: null,
        plotMutations: { showTrendline: true },
        series_builder_misc: {
          selectedMetricId: null,
          activeCategory: "GPS",
          showAdvancedDate: false,
          dateRangePreset: "last4weeks",
          inheritDateRange: true,
          inheritPlayers: true,
        },
      },
    ],
  };
}

export function mockChartResponse(widgetId: string): WidgetResponseSpec {
  return {
    widget_id: widgetId,
    series: [
      {
        query_id: "q-sprint",
        name: "SPRINT DISTANCE AVG",
        data: [
          { value: 412, category: "Mon", hex: "#1a73e8" },
          { value: 389, category: "Tue", hex: "#1a73e8" },
          { value: 445, category: "Wed", hex: "#1a73e8" },
          { value: 378, category: "Thu", hex: "#1a73e8" },
          { value: 401, category: "Fri", hex: "#1a73e8" },
          { value: 215, category: "Sat", hex: "#1a73e8" },
          { value: 310, category: "Sun", hex: "#1a73e8" },
        ],
      },
      {
        query_id: "q-speed",
        name: "TOP SPEED AVG",
        data: [
          { value: 31.2, category: "Mon" },
          { value: 29.8, category: "Tue" },
          { value: 32.1, category: "Wed" },
          { value: 30.5, category: "Thu" },
          { value: 33.0, category: "Fri" },
          { value: 27.3, category: "Sat" },
          { value: 28.9, category: "Sun" },
        ],
      },
    ],
  };
}
